import { MapPin, Clock, Phone, Mail, Utensils, Cake } from 'lucide-react'
import useInView from '../../hooks/useInView'

const BRANCHES = [
  {
    name: 'Desres Kitchen',
    icon: Utensils,
    location: 'Limpopo, South Africa',
    description: 'Our flagship branch, running for over 25 years. African cuisine, daily bakes, sweet treats, customised cakes, and more.',
    hours: [
      { days: 'Monday - Saturday', time: '07:00 - 18:00' },
      { days: 'Sunday', time: '08:00 - 15:00' },
    ],
  },
  {
    name: "Yahweh's Delight",
    icon: Cake,
    location: 'Pretoria, South Africa',
    description: 'Our newly opened bakery branch in Pretoria, bringing the family tradition to a new community. Fresh bread, scones, fatcook, rolls, sandwiches, and kotas daily.',
    hours: [
      { days: 'Monday - Saturday', time: '06:00 - 18:00' },
      { days: 'Sunday', time: '07:00 - 14:00' },
    ],
  },
]

function ScrollReveal({ children, delay = 0, className = '' }) {
  const [ref, inView] = useInView()
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.7s ease-out ${delay}s, transform 0.7s ease-out ${delay}s`,
      }}
    >
      {children}
    </div>
  )
}

export default function Contact() {
  return (
    <div>
      {/* Page header */}
      <section className="bg-charcoal relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-6 h-0.5 bg-terracotta" />
            <span className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta">Find Us</span>
          </div>
          <h1 className="font-display text-cream tracking-[0.04em] leading-[0.95]" style={{ fontSize: 'clamp(48px, 6vw, 88px)' }}>
            Contact
          </h1>
          <p className="text-sm text-muted mt-4 max-w-xl">
            Visit us at any of our two branches. We'd love to serve you!
          </p>
        </div>
      </section>

      <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />

      {/* Branch cards */}
      <section className="bg-umber">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {BRANCHES.map((branch, i) => {
              const Icon = branch.icon
              return (
                <ScrollReveal key={branch.name} delay={0.2 + i * 0.15}>
                  <div className="relative bg-surface border border-border-dark rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:border-terracotta/40 transition-colors overflow-hidden">
                    <div className="p-8">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 bg-surface-high rounded-xl flex items-center justify-center">
                          <Icon size={22} className="text-ink-mid" />
                        </div>
                        <h2 className="font-display text-cream text-xl tracking-wide">{branch.name}</h2>
                      </div>

                      <p className="text-sm text-ink-mid mb-6 leading-relaxed">{branch.description}</p>

                      <div className="flex items-start gap-3 mb-4">
                        <MapPin size={16} className="mt-0.5 text-muted" />
                        <div>
                          <p className="text-sm font-body font-medium text-cream">Location</p>
                          <p className="text-sm text-muted">{branch.location}</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Clock size={16} className="mt-0.5 text-muted" />
                        <div>
                          <p className="text-sm font-body font-medium text-cream mb-2">Operating Hours</p>
                          <div className="space-y-2">
                            {branch.hours.map((h) => (
                              <div key={h.days} className="flex justify-between gap-6 text-sm">
                                <span className="text-muted">{h.days}</span>
                                <span className="text-cream font-body font-medium">{h.time}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              )
            })}
          </div>
        </div>
      </section>

      <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />

      {/* Get in touch */}
      <section className="bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28">
          <ScrollReveal>
            <div className="relative bg-surface border border-border-dark rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.07)] overflow-hidden max-w-3xl mx-auto">
              <div className="p-10 text-center">
                <h2 className="font-display text-cream text-2xl tracking-wide mb-8">Get in Touch</h2>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-muted" />
                    <span className="text-sm text-ink-mid">Contact us at our branches</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail size={18} className="text-muted" />
                    <span className="text-sm text-ink-mid">desreskitchen@info.co.za</span>
                  </div>
                </div>
                <p className="text-xs text-muted mt-8">
                  For catering, customised cakes, bulk orders, or enquiries, reach out via phone or email.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}
